import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import { handleDropDowns } from "../../config/dropDownHelpers";
import { FaTrash } from "react-icons/fa";
import { HiOutlineAdjustments } from "react-icons/hi";
import { RiSettings3Line } from "react-icons/ri";
import { MdOutlinePercent } from "react-icons/md";
import { FaRupeeSign } from "react-icons/fa";

import {
  generateShortItemCode,
  threeDecimal,
  twoDecimal,
} from "../../config/helperFunctions";
import { Diamond, Gem } from "lucide-react"; // stone icon
import { GiCutDiamond, GiStonePile } from "react-icons/gi"; // Better diamond icon
import { GiStoneCrafting } from "react-icons/gi";
import { FaGem } from "react-icons/fa";
import { MdPhotoCamera } from "react-icons/md";
// import StoneDiamondModal from "./StoneDiamondModal";
// import StonePopUp from "./StonePopUp";
// import DiamondPopUp from "./DiamondPopUp";
import { useFieldArray } from "react-hook-form";

const PurchaseStockItemForm = ({
  control,
  labelledStockFields,
  rowindex,
  register,
  errors,
  watch,
  firstFieldRef,
  onDelete,
  getValues,
  setValue,
  onRowFocus,
  invoiceNo,
}) => {
  const categoriesFromState = useSelector(
    (state) => state.categories?.categories
  );
  const productsFromState = useSelector((state) => state.products?.products);
  const vendorsFromState = useSelector((state) => state.vendors?.vendors);
  const designFromState = useSelector((state) => state.designs?.designs);
  const purityFromState = useSelector((state) => state.purities?.purities);
  const branchesFromState = useSelector((state) => state.branches?.branches);
  const [openStoneModal, setOpenStoneModal] = useState(false);
  const [openDiamondModal, setOpenDiamondModal] = useState(false);

  const category = watch(`stockEntries.${rowindex}.category`);
  const product = watch(`stockEntries.${rowindex}.product`);
  const design = watch(`stockEntries.${rowindex}.design`);
  const totalWt = watch(`stockEntries.${rowindex}.totalWt`);
  const packingWt = watch(`stockEntries.${rowindex}.packingWt`);
  const stoneWt = watch(`stockEntries.${rowindex}.stoneWt`);
  const diamondWt = watch(`stockEntries.${rowindex}.diamondWt`);
  const stoneValues = watch(`stockEntries.${rowindex}.stones`);
  const rateCut = watch(`stockEntries.${rowindex}.rateCut`);
  const makingOnFine = watch(`stockEntries.${rowindex}.makingOnFine`);
  const itemFineMakingWt = watch(`stockEntries.${rowindex}.itemFineMakingWt`);
  const totalItemAmount = watch(`stockEntries.${rowindex}.totalItemAmount`);

  // set initial itemCode once
  // useEffect(() => {
  //   setValue(`stockEntries.${rowindex}.itemCode`, generateShortItemCode());
  // }, [rowindex, setValue]);

  const formRef = useRef(null);

  // Ctrl + Delete to remove this row (when focused inside this card)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "Delete") {
        if (
          formRef.current &&
          formRef.current.contains(document.activeElement)
        ) {
          e.preventDefault();
          onDelete?.();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onDelete]);

  // ========== derived weights ==========
  // useEffect(() => {
  //   const gross = ((+totalWt || 0) - (+packingWt || 0)).toFixed(3);
  //   const net = (
  //     (+totalWt || 0) -
  //     (+packingWt || 0) -
  //     (+stoneWt || 0) -
  //     (+diamondWt || 0)
  //   ).toFixed(3);

  //   setValue(`stockEntries.${rowindex}.grossWt`, gross);
  //   setValue(`stockEntries.${rowindex}.netWt`, net);
  // }, [totalWt, packingWt, stoneWt, diamondWt, setValue, rowindex]);

  const handleCalculations = ({ rowindex, getValues, setValue }) => {
    const entry = (field) => `stockEntries.${rowindex}.${field}`;
    const get = (key) => parseFloat(getValues(entry(key)) || 0);

    const isChecked = !!getValues(entry("rateCut")); // amount-mode flag
    const makingOnFine = !!getValues(entry("makingOnFine")); // wastage on fine flag

    const totalWt = get("totalWt");
    const packingWt = get("packingWt");
    const stoneWt = get("stoneWt");
    const diamondWt = get("diamondWt");
    const ratePerGram = get("ratePerGram");
    const finePercent = get("finePercent");
    const makingPercentage = get("makingPercentage");
    const makingPerGram = get("makingPerGram");
    const fixedMaking = get("fixedMaking"); // in grams
    const fixedAmount = get("fixedAmount"); // in currency
    const totalStoneAmount = get("totalStoneAmount");
    const totalDiamondAmount = get("totalDiamondAmount");
    const hallmarkCharge = get("hallmarkCharge");

    const grossWt = +(totalWt - packingWt).toFixed(3);
    const netWt = +(grossWt - stoneWt - diamondWt).toFixed(3);

    // Fine weight from purity %
    const fineWt = +((finePercent / 100) * netWt).toFixed(3);

    // ✅ Base for wastage/making%: Net Wt OR Fine Wt depending on toggle
    const makingBaseWt = makingOnFine ? fineWt : netWt;

    // ===== Amount mode (rateCut ON) =====
    const metalAmount = isChecked ? ratePerGram * netWt : 0;

    // Per-gram making stays on net weight (usually), change if your rule differs
    const amountByMakingPerGram = makingPerGram * netWt;

    // ✅ Percentage making on value of metal with correct base weight
    const amountByPercentage = isChecked
      ? makingBaseWt * ratePerGram * (makingPercentage / 100)
      : 0;

    // Fixed making (grams) converted to money in amount-mode
    const amountByFixedMaking = isChecked ? fixedMaking * ratePerGram : 0;

    const totalMakingAmount =
      amountByMakingPerGram +
      fixedAmount +
      amountByPercentage +
      amountByFixedMaking;

    const totalItemAmount =
      metalAmount +
      totalMakingAmount +
      totalStoneAmount +
      totalDiamondAmount +
      hallmarkCharge;

    setValue(entry("grossWt"), grossWt);
    setValue(entry("netWt"), netWt);
    setValue(entry("totalItemMakingAmount"), threeDecimal(totalMakingAmount));
    setValue(entry("totalItemAmount"), threeDecimal(totalItemAmount));

    // ===== Weight mode (rateCut OFF): show fine/making weights =====
    if (!isChecked) {
      // ✅ Wastage% base also switches here
      const makingWt = (makingPercentage / 100) * makingBaseWt + fixedMaking;
      const fineMakingWt = fineWt + makingWt;

      setValue(entry("itemFineWt"), threeDecimal(fineWt));
      setValue(entry("itemMakingWt"), threeDecimal(makingWt));
      setValue(entry("itemFineMakingWt"), threeDecimal(fineMakingWt));
    } else {
      setValue(entry("itemFineWt"), "");
      setValue(entry("itemMakingWt"), "");
      setValue(entry("itemFineMakingWt"), "");
    }
  };

  return (
    <>
      <motion.div
        ref={formRef}
        onFocusCapture={() => onRowFocus?.()}
        tabIndex={-1}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: rowindex * 0.05 }}
        className="mr-2 p-4 border border-gray-200 hover:border-gray-300 shadow-lg rounded-lg mb-4 bg-white relative"
      >
        {/* Header */}
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">
            #{rowindex + 1} • Stock Entry
          </h3>
          <div className="flex-1 h-px bg-gray-200 mx-2" />
          <button
            type="button"
            onClick={onDelete}
            className="text-gray-500 hover:text-red-600 transition-all duration-200 hover:scale-110"
            title="Delete this item"
          >
            <FaTrash className="text-[14px]" />
          </button>
        </div>

        {/* Fields Grid */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-2">
          {labelledStockFields?.map((item, idx) => {
            const colSpan = [
              "category",
              "product",
              "design",
              "description",
            ].includes(item?.name)
              ? "md:col-span-2"
              : "md:col-span-1";

            const path = `stockEntries.${rowindex}.${item.name}`;
            const rules = {
              required:
                // item?.mandatory ? `${item.label} is required` :
                false,
            };

            return (
              <div key={idx} className={colSpan}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {item?.label}{" "}
                  {item?.mandatory && <span className="text-red-500">*</span>}
                </label>

                {/* TEXT */}
                {item?.type === "text" && (
                  <div className="relative w-full">
                    <input
                      {...register(path, {
                        ...rules,
                        onChange: (e) => {
                          const value = e.target.value;
                          setValue(path, value);
                          handleCalculations({ rowindex, getValues, setValue });
                        },
                      })}
                      onFocus={() => onRowFocus?.()}
                      ref={(el) => {
                        register(path, rules).ref(el);
                        if (idx === 0 && firstFieldRef)
                          firstFieldRef.current = el;
                      }}
                      disabled={
                        item.name === "grossWt" || item.name === "netWt"
                      }
                      placeholder={item?.placeholder || ""}
                      className={`w-full pl-1 py-[5px] ${
                        item?.name == "ratePerGram" ||
                        item?.name == "makingPercentage"
                          ? "pr-5"
                          : "pr-1"
                      } text-sm border border-gray-300 rounded-md bg-white placeholder:text-gray-400
        focus:outline-none focus:ring-2 ${
          invoiceNo ? "focus:ring-green-400" : "focus:ring-red-400"
        } focus:border-violet-300`}
                    />
                    {item?.name == "ratePerGram" && (
                      <button
                        title="Rate cut"
                        type="button"
                        tabIndex={0}
                        onClick={() => {
                          const current = getValues(
                            `stockEntries.${rowindex}.rateCut`
                          );
                          setValue(
                            `stockEntries.${rowindex}.rateCut`,
                            !current
                          );
                          handleCalculations({ rowindex, getValues, setValue }); // ✅ recalculate immediately
                        }}
                        className={`absolute  inset-y-0 right-0 flex items-center 
    ${rateCut ? "text-red-800" : "text-gray-400"}
       hover:text-red-800 ${
         invoiceNo
           ? "focus:ring-green-400 focus:outline-green-400"
           : "focus:ring-red-400 focus:outline-red-400"
       }`}
                      >
                        <FaRupeeSign className="text-sm mx-1" />
                      </button>
                    )}
                    {item?.name == "makingPercentage" && (
                      <button
                        title="Making on fine wt"
                        type="button"
                        tabIndex={0}
                        onClick={() => {
                          const curr = !!getValues(
                            `stockEntries.${rowindex}.makingOnFine`
                          );
                          setValue(
                            `stockEntries.${rowindex}.makingOnFine`,
                            !curr
                          );
                          handleCalculations({ rowindex, getValues, setValue }); // ✅ recalc now
                        }}
                        className={`absolute inset-y-0 right-0 flex items-center 
      ${makingOnFine ? "text-red-800" : "text-gray-400"}
      hover:text-red-800 ${
        invoiceNo
          ? "focus:ring-green-400 focus:outline-green-400"
          : "focus:ring-red-400 focus:outline-red-400"
      }`}
                      >
                        <HiOutlineAdjustments className="text-sm mx-1" />
                      </button>
                    )}

                    {errors?.stockEntries?.[rowindex]?.[item.name] && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.stockEntries[rowindex][item.name].message}
                      </p>
                    )}
                  </div>
                )}

                {/* NUMBER */}
                {item?.type === "number" && (
                  <>
                    <input
                      onFocus={() => onRowFocus?.()}
                      type="number"
                      {...register(path, rules)}
                      ref={(el) => {
                        register(path, rules).ref(el);
                        if (idx === 0 && firstFieldRef)
                          firstFieldRef.current = el;
                      }}
                      placeholder={item?.placeholder || ""}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white placeholder:text-gray-400
                               focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300"
                    />
                    {errors?.stockEntries?.[rowindex]?.[item.name] && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.stockEntries[rowindex][item.name].message}
                      </p>
                    )}
                  </>
                )}

                {/* DATE */}
                {item?.type === "date" && (
                  <>
                    <input
                      onFocus={() => onRowFocus?.()}
                      type="date"
                      {...register(path, rules)}
                      ref={(el) => {
                        register(path, rules).ref(el);
                        if (idx === 0 && firstFieldRef)
                          firstFieldRef.current = el;
                      }}
                      className="w-full px-3 h-8 text-sm border border-gray-300 rounded-md bg-white
                               focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300"
                    />
                    {errors?.stockEntries?.[rowindex]?.[item.name] && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.stockEntries[rowindex][item.name].message}
                      </p>
                    )}
                  </>
                )}

                {/* DROPDOWN */}
                {item?.type === "dropdown" && (
                  <>
                    <Controller
                      name={path}
                      control={control}
                      rules={rules}
                      render={({ field }) => (
                        <Select
                          {...field}
                          ref={(ref) => {
                            field.ref(ref);
                            if (idx === 0 && firstFieldRef) {
                              firstFieldRef.current = {
                                focus: () => ref?.focus?.(),
                              };
                            }
                          }}
                          onFocus={() => onRowFocus?.()}
                          options={handleDropDowns({
                            key: item.name,
                            vendors: vendorsFromState,
                            categories: categoriesFromState,
                            products: productsFromState,
                            designs: designFromState,
                            purities: purityFromState,
                            branches: branchesFromState,
                            category,
                            product,
                          })}
                          classNamePrefix="react-select"
                          menuPlacement="bottom"
                          isClearable
                          isSearchable
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              minHeight: "32px",
                              height: "32px",
                              borderColor: state.isFocused
                                ? "#A78BFA"
                                : "#D1D5DB", // violet-300 / gray-300
                              boxShadow: state.isFocused
                                ? "0 0 0 2px rgba(167,139,250,0.35)"
                                : "none",
                              fontSize: "12px",
                              backgroundColor: "white",
                            }),
                            indicatorsContainer: (base) => ({
                              ...base,
                              height: "30px",
                            }),
                            dropdownIndicator: (base) => ({
                              ...base,
                              padding: "4px",
                            }),
                            clearIndicator: (base) => ({
                              ...base,
                              padding: "4px",
                            }),
                            valueContainer: (base) => ({
                              ...base,
                              padding: "0 6px",
                              height: "30px",
                            }),
                            input: (base) => ({
                              ...base,
                              margin: 0,
                              padding: 0,
                            }),
                            menu: (base) => ({ ...base, fontSize: "12px" }),
                            menuList: (base) => ({
                              ...base,
                              maxHeight: "140px",
                              overflowY: "auto",
                            }),
                            singleValue: (base) => ({
                              ...base,
                              fontSize: "12px",
                            }),
                          }}
                        />
                      )}
                    />
                    {errors?.stockEntries?.[rowindex]?.[item.name] && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.stockEntries[rowindex][item.name].message}
                      </p>
                    )}
                  </>
                )}

                {item?.type === "button" && item?.name == "stones" && (
                  <div className="relative w-fit mt-3">
                    <AnimatePresence>
                      {/* {openStoneModal && (
                        <StonePopUp
                          key="stone-popup"
                          openStoneModal={openStoneModal}
                          setOpenStoneModal={setOpenStoneModal}
                          initialStones={
                            watch(`stockEntries.${rowindex}.stones`) || []
                          }
                          onSave={(stones) => {
                            let pcs = 0;
                            let amt = 0;
                            let wt = 0;
                            for (const s of stones || []) {
                              console.log(s);
                              const p = Number(s?.stnPopupPcs) || 0;
                              const a = Number(s?.stnPopupAmt) || 0;
                              const w = s?.stnPopupDeduct == "deduct"  ? (
                                s?.unit?.value == "gram"
                                  ? Number(s?.stnPopupWt) || 0
                                  : (Number(s?.stnPopupWt) || 0) / 5 ) : 0;
                              pcs += p;
                              amt += a;
                              wt += w;
                            }
                            setValue(`stockEntries.${rowindex}.stones`, stones);
                            setValue(
                              `stockEntries.${rowindex}.stonePcs`,
                              String(pcs)
                            );
                            setValue(
                              `stockEntries.${rowindex}.totalStoneAmount`,
                              twoDecimal(amt)
                            );
                            setValue(
                              `stockEntries.${rowindex}.stoneWt`,
                              threeDecimal(wt)
                            );
                          }}
                        />
                      )} */}
                    </AnimatePresence>

                    <motion.div
                      onClick={() => setOpenStoneModal((prev) => !prev)}
                      className="cursor-pointer text-violet-600"
                      animate={{ y: [0, -5, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <button type="button">
                        <GiCutDiamond size={20} />
                      </button>
                    </motion.div>
                  </div>
                )}

                {item?.type === "button" && item?.name == "diamonds" && (
                  <div className="relative w-fit mt-3">
                    {/* {openDiamondModal && (
                      <DiamondPopUp
                        setOpenDiamondModal={setOpenDiamondModal}
                        openDiamondModal={openDiamondModal}
                      />
                    )} */}

                    <motion.div
                      onClick={() => setOpenDiamondModal((prev) => !prev)}
                      className="cursor-pointer text-violet-600"
                      animate={{ y: [0, -5, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <button type="button">
                        <GiCutDiamond size={20} />
                      </button>
                    </motion.div>
                  </div>
                )}

                {item?.type === "button" && item?.name === "images" && (
                  <button
                    type="button"
                    className="relative w-fit cursor-pointer bg-gray-100  rounded-md group overflow-hidden"
                  >
                    <MdPhotoCamera size={28} className="text-blue-500" />
                  </button>
                )}
              </div>
            );
          })}

          {/* // item?.type === "button" && item?.name =="stones" && ( */}

          {/* ) */}
          {/* } */}
        </div>
      </motion.div>
    </>
  );
};

export default PurchaseStockItemForm;
